import {AuditScreen} from "./screens/AuditScreen";
import { WorkspaceFooter } from "./ui/Workspace";
import { OverviewScreen } from "./screens/OverviewScreen";
import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { AppShell } from "./layout/AppShell";
import { ApiError, can, httpBackend, type Backend, type CompanyBranding, type CompanyPreferences, type Identity } from "./api";
import { canAccessRoute, createRouter, parseRoute, type AppRoute } from "./router";
import { handleSessionError } from "./session";
import { TicketsScreen } from "./screens/TicketsScreen";
import { InventoryScreen } from "./screens/InventoryScreen";
import { PartsScreen } from "./screens/PartsScreen";
import { PurchasesScreen } from "./screens/PurchasesScreen";
import { BillingScreen } from "./screens/BillingScreen";
import { DocumentsScreen } from "./screens/DocumentsScreen";
import { NotificationsScreen } from "./screens/NotificationsScreen";
import { FoundationScreen } from "./screens/FoundationScreen";
import { AssetsScreen } from "./screens/AssetsScreen";
import { LicensesScreen } from "./screens/LicensesScreen";
import { CustomersScreen } from "./screens/CustomersScreen";
import { EquipmentScreen } from "./screens/EquipmentScreen";
import { ContractsScreen } from "./screens/ContractsScreen";
import { ServicesScreen } from "./screens/ServicesScreen";
import { TeamScreen } from "./screens/TeamScreen";
import { SettingsScreen } from "./screens/SettingsScreen";
import { ThemePicker } from './ui/ThemePicker';

type Phase = "loading" | "login" | "ready";

export function App({ backend = httpBackend }: { backend?: Backend }) {
  const [ticketCount,setTicketCount]=useState<number|undefined>(undefined);
  const [branding,setBranding]=useState<CompanyBranding|null>(null);

  const [phase, setPhase] = useState<Phase>("loading");
  const [identity, setIdentity] = useState<Identity | null>(null);
  useEffect(()=>{setBranding(null);setTicketCount(undefined);if(!identity)return;let active=true;backend.companyBranding?.().then(value=>{if(active)setBranding(value);}).catch(()=>{});return()=>{active=false;};},[backend,identity]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [inviteToken] = useState(() => new URLSearchParams(window.location.hash.slice(1)).get('invite') ?? '');
  const [registration, setRegistration] = useState(() => Boolean(new URLSearchParams(window.location.hash.slice(1)).get('invite')));
  const [route, setRoute] = useState<AppRoute>("overview");
  const navigateRef = useRef<(route: AppRoute, query?:Record<string,string>) => void>(() => undefined);

  useEffect(() => { if (inviteToken) window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search); }, [inviteToken]);

  const clearSession = useCallback(() => {
    setIdentity(null);
    setBranding(null);
    setTicketCount(undefined);
    setPhase("login");
  }, []);

  // A downstream 401 can mean a missing application mapping in that service.
  // Only Identity can decide whether the browser session itself has expired.
  const verifySession = useCallback(() => {
    void backend.me().catch((cause: unknown) => {
      if (cause instanceof ApiError && cause.status === 401) clearSession();
    });
  }, [backend, clearSession]);

  useEffect(() => {
    let active = true;
    backend.me().then(user => {
      if (!active) return;
      setIdentity(user);
      setPhase("ready");
    }).catch((cause: unknown) => {
      if (!active) return;
      if (handleSessionError(cause, clearSession) === "unauthorized") return;
      else setError("Сервис авторизации временно недоступен");
    });
    return () => { active = false; };
  }, [backend]);

  useEffect(() => {
    if (!identity) return;
    const router = createRouter(identity, setRoute);
    const initial = parseRoute(window.location.pathname);
    setRoute(initial);
    navigateRef.current = router.navigate;
    return router.dispose;
  }, [identity]);


  async function login(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError("");
    try {
      const user = await backend.login(String(form.get("email")), String(form.get("password")), String(form.get("applicationCode")));
      setIdentity(user);
      setPhase("ready");
    } catch (cause) {
      setError(cause instanceof ApiError && cause.status === 401 ? "Неверный логин, пароль или код приложения" : "Не удалось выполнить вход");
    } finally {
      setBusy(false);
    }
  }

  async function acceptInvitation(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true); setError("");
    try {
      const user = await backend.acceptInvitation!(String(form.get("token")).trim(), String(form.get("password")), String(form.get("displayName")).trim());
      setIdentity(user); setPhase("ready");
    } catch (cause) {
      setError(cause instanceof ApiError && cause.code === "invalid_invitation" ? "Приглашение недействительно или срок его действия истёк" : cause instanceof ApiError && cause.code === "weak_password" ? "Для новой учётной записи задайте пароль не короче 12 символов." : cause instanceof ApiError && cause.code === "invalid_credentials" ? "Неверный пароль существующей учётной записи." : "Не удалось принять приглашение");
    } finally { setBusy(false); }
  }

  async function logout() {
    await backend.logout().catch(() => undefined);
    clearSession();
  }


  if (phase === "loading") return <main className="center"><div className="loader" aria-label="Загрузка" /></main>;
  if (phase === "login") return <main className="login-page"><div className="login-appearance"><ThemePicker/></div><form className="login-card" onSubmit={registration ? acceptInvitation : login}>
    <div className="brand-mark">К</div>
    <p className="eyebrow">СОБСТВЕННЫЙ СЕРВЕР</p>
    <h1>{registration ? "Принять приглашение" : "Вход в Контур CRM"}</h1>
    <p className="muted">{registration ? "Введите токен из письма. Для новой учётной записи задайте пароль не короче 12 символов. Если аккаунт уже есть, используйте его текущий пароль (от 8 символов)." : "Используйте учётную запись вашего рабочего пространства."}</p>
    {registration ? <><label>Токен приглашения<input name="token" autoComplete="off" defaultValue={inviteToken} required /></label><label>Ваше имя<input name="displayName" required /></label></> : <><label>Логин или почта<input name="email" type="text" autoComplete="username" required /></label><label>Код приложения<input name="applicationCode" defaultValue="crm" required /></label></>}
    <label>Пароль<input name="password" type="password" autoComplete={registration ? "new-password" : "current-password"} minLength={registration ? 8 : undefined} required /></label>
    {error && <p className="error" role="alert">{error}</p>}
    <button className="primary" disabled={busy}>{busy ? "Подождите…" : registration ? "Принять приглашение" : "Войти"}</button>
    <button className="button-secondary auth-switch" type="button" disabled={busy} onClick={() => { setError(""); setRegistration(value => !value); }}>{registration ? "Уже есть доступ? Войти" : "Есть приглашение? Зарегистрироваться"}</button>
  </form></main>;

  if (!identity) return null;
  return <AppShell identity={identity} branding={branding} ticketCount={ticketCount} route={route} navigate={next => navigateRef.current(next)} logout={logout}>

      {error && <div className="error banner" role="alert">{error}</div>}
      {route === "overview" ? <OverviewScreen backend={backend} identity={identity} onTicketCount={setTicketCount} navigate={(next,query)=>navigateRef.current(next,query)} onUnauthorized={verifySession}/> : <>
        <RouteScreen route={route} backend={backend} identity={identity} onOpenTickets={status=>navigateRef.current("tickets",{status})} onTicketCount={setTicketCount} onCompanySaved={value=>setBranding({displayName:value.displayName||value.legalName,logoDataUrl:value.logoDataUrl})} onUnauthorized={verifySession}/><WorkspaceFooter/>
      </>}
  </AppShell>;
}

function RouteScreen({route,backend,identity,onCompanySaved,onUnauthorized,onTicketCount,onOpenTickets}:{onOpenTickets:(status:string)=>void;onTicketCount?:(count:number)=>void;route:AppRoute;backend:Backend;identity:Identity;onCompanySaved?:(value:CompanyPreferences)=>void;onUnauthorized:()=>void}) {
  if(!canAccessRoute(route,identity)) return <section className="screen"><div className="error" role="alert">Недостаточно прав для просмотра этого раздела.</div></section>;
  switch(route){
    case "audit": return <AuditScreen backend={backend} identity={identity} onUnauthorized={onUnauthorized}/>;
    case "tickets": return <TicketsScreen backend={backend} identity={identity} onTicketCount={onTicketCount} canCreate={(can(identity,"crm.manage")||can(identity,"crm.tickets.assign"))&&(can(identity,"crm.manage")||can(identity,"crm.tickets.write"))} canReport={can(identity,"crm.manage")||can(identity,"crm.tickets.write")} canSeeCost={can(identity,"crm.manage")||can(identity,"billing.internal")} onUnauthorized={onUnauthorized}/>;
    case "inventory": return <InventoryScreen backend={backend} identity={identity} onUnauthorized={onUnauthorized}/>;
    case "parts": return <PartsScreen backend={backend} identity={identity} onUnauthorized={onUnauthorized}/>;
    case "purchases": return <PurchasesScreen backend={backend} identity={identity} onUnauthorized={onUnauthorized}/>;
    case "billing": return <BillingScreen backend={backend} identity={identity} onOpenTickets={onOpenTickets} onUnauthorized={onUnauthorized}/>;
    case "documents": return <DocumentsScreen backend={backend} userId={identity.id} onUnauthorized={onUnauthorized}/>;
    case "notifications": return <NotificationsScreen backend={backend} onUnauthorized={onUnauthorized} canConfigureCompany={identity.permissions.includes("crm.company.write")}/>;
    case "access": return <TeamScreen backend={backend} identity={identity} onUnauthorized={onUnauthorized}/>;
    case "customers": return <CustomersScreen backend={backend} canWrite={can(identity,"crm.customers.write")} onUnauthorized={onUnauthorized}/>;
    case "equipment": return <EquipmentScreen backend={backend} canWrite={can(identity,"crm.equipment.write")} onUnauthorized={onUnauthorized}/>;
    case "contracts": return <ContractsScreen backend={backend} canWrite={can(identity,"crm.contracts.write")} onUnauthorized={onUnauthorized}/>;
    case "services": return <ServicesScreen backend={backend} canWrite={can(identity,"crm.catalog.write")} canSeeCost={can(identity,"billing.internal")} onUnauthorized={onUnauthorized}/>;
    case "team": return <TeamScreen backend={backend} identity={identity} onUnauthorized={onUnauthorized}/>;
    case "assets": return <AssetsScreen backend={backend} identity={identity} onUnauthorized={onUnauthorized}/>;
    case "licenses": return <LicensesScreen backend={backend} identity={identity} onUnauthorized={onUnauthorized}/>;
    case "settings": return <SettingsScreen backend={backend} onCompanySaved={onCompanySaved} canWrite={can(identity,"crm.company.write")||can(identity,"company.settings")} canExport={can(identity,"crm.export.read")||can(identity,"crm.manage")} onUnauthorized={onUnauthorized}/>;
    default: return null;
  }
}
